import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../redux/axios";
import { encrypted } from "../Common/encrypted";
import {
  removeItem,
  removeItemWithExpiry,
  storeItem,
} from "../../lib/utility";
import { isMultiAccessUser } from "../User/Abac/abacAccess";
import {
  extractPermissionsFromResponse,
  matchAccessMemberId,
  resolveAbacAccessId,
  resolveAbacAccessName,
  resolveAbacListUserId,
} from "../User/Abac/abacApi";

const initialState = {
  user: {},
  accessDevice: false,
  otpScreen: false,
  confirmOtp: false,
  accountExpired: false,
  isFetching: false,
  isLoginSuccess: false,
  isOTPSuccess: false,
  isAccessDeviceSuccess: false,
  isError: false,
  errorMessage: "",
  jwtLoginMeta: null,

  navOptions: {},
  isFetchingNavOptions: false,
  isnavOptionsFetchSuccess: false,
  isnavOptionsFetchError: false,
  navOptionsFetchErrorMessage: "",

  isFetchingLandingInfo: false,

  isFetchingLandingInfoCreate: false,
  isSuccessCreateLandingInfo: false,

  isFetchingLoginPanelSave: false,
  isErrorUpdateLandingInfo: false,
  updateLandingInfoErrorMsg: "",
  isFetchingLandingInfoAll: false,

  /**
   * Multi-access session ABAC tree.
   * null = not loaded yet → gates fail-open until REST bootstrap or WS push.
   * Live updates arrive via `/user/queue/abac-permissions` (ABAC_PERMISSIONS_UPDATED).
   */
  abacPermissions: null,
  /** Numeric multi-access row id used for GET /multiUser/permissions/{id}. */
  abacAccessKey: null,
  abacSessionLoaded: false,
  abacSessionOk: false,
  isFetchingAbacSession: false,
};

function clearStaleAuthSession() {
  removeItemWithExpiry("profileData");
  removeItem("userId");
  sessionStorage.removeItem("forgotPasswordUserName");
  globalThis.profileData = null;
  globalThis.isAuthenticated = false;
  globalThis.isFirstTime = true;
}

function resetAbacSessionFields(state) {
  state.abacPermissions = null;
  state.abacAccessKey = null;
  state.abacSessionLoaded = false;
  state.abacSessionOk = false;
  state.isFetchingAbacSession = false;
}

function applyLoginSuccess(state, data) {
  clearStaleAuthSession();
  state.otpScreen = false;
  state.accessDevice = false;
  state.accountExpired = false;
  state.confirmOtp = false;
  const accessName = String(data?.accessName ?? data?.accessname ?? "").trim();
  // Omni: only access-name logins become MA. Normal BroadAdmin/tenant login
  // has no accessName, so ABAC never starts hiding buttons.
  state.user = {
    ...data,
    ...(accessName
      ? {
        accessName,
        isMultiAccessMember: true,
      }
      : {}),
  };
  resetAbacSessionFields(state);
  state.isLoginSuccess = true;
  globalThis.isAuthenticated = true;
  if (data?.id != null) {
    storeItem("userId", data.id);
  }
  // Keep existing /login/jwt requestUrl; never overwrite with other APIs
  if (state.jwtLoginMeta?.requestUrl) {
    state.jwtLoginMeta = {
      ...state.jwtLoginMeta,
      response: {
        accessName: data?.accessName ?? "",
        isMultiSessionAllowed: data?.isMultiSessionAllowed,
        accessToken: data?.accessToken ?? "",
      },
      capturedAt: state.jwtLoginMeta.capturedAt ?? Date.now(),
    };
  }
}

const getIPAddress = async () => {
  try {
    let { data } = await axios.get("https://api.ipify.org/");
    if (data) return data;
  } catch (err) {
    showToast(
      "Error Occured while getting your IP. Please Refresh page.",
      "error"
    );
  }
};
export const loginUser = createAsyncThunk(
  "auth/login",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      const encryptedData = encrypted({ ...udata, ipAddress });
      const response = await axios.post(
        "/login/jwt",
        {},
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers, config } = response;
      const loginRequestParam = config?.params?.loginRequest;
      const query =
        loginRequestParam != null
          ? `?${new URLSearchParams({
              loginRequest: loginRequestParam,
            }).toString()}`
          : "";
      // Prefer the real absolute URL (same as Network tab); fall back to baseURL + path
      const requestUrl =
        response.request?.responseURL ||
        `${(config?.baseURL || import.meta.env.VITE_REACT_APP_API_URL || "").replace(/\/$/, "")}/login/jwt${query}`;

      if (response.status === 200) {
        return {
          data,
          status: headers.get("X-Custom-Info") ?? status,
          requestUrl,
        };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let accessToken = auth.user.accessToken;
      const response = await axios.post(
        "/logout",
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      let { data, status, headers } = response;

      if (response.status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      let encryptedData = encrypted({ ...udata, ipAddress });

      const response = await axios.post(
        "/login/verifyOtp",
        {},
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers } = response;

      if (status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const accessDeviceThunk = createAsyncThunk(
  "auth/authenticateMultiUser",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      let encryptedData = encrypted({ ...udata, ipAddress });
      const response = await axios.post(
        "/login/authenticateMultiUser",
        encryptedData,
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers } = response;

      if (status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const accessDeviceSkip = createAsyncThunk(
  "auth/accessDeviceSkip",
  async (udata, thunkAPI) => {
    try {
      const ipAddress = await getIPAddress();
      let encryptedData = encrypted({ ...udata, ipAddress });

      const response = await axios.post(
        "/login/authenticateSkip",
        encryptedData,
        {
          params: {
            loginRequest: encryptedData,
          },
        }
      );
      let { data, status, headers } = response;

      if (status === 200) {
        return { data, status: headers.get("X-Custom-Info") };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);


/**
 * Resolve numeric multi-access row id for session permissions.
 * Prefer explicit accessId; otherwise match accessName via list/access.
 */
async function resolveSessionAccessId(user, tokenStr) {
  let accessId = resolveAbacAccessId(user);
  if (accessId) return accessId;

  const accessName = resolveAbacAccessName(user);
  const listUserId = resolveAbacListUserId(user);
  if (!accessName || !listUserId || !tokenStr) return null;

  try {
    const { data, status } = await axios.get(
      `/multiUser/list/access?userId=${listUserId}&pageNumber=0&pageSize=200`,
      { headers: { Authorization: `Bearer ${tokenStr}` } }
    );
    if (!status.toString().startsWith("2")) return null;
    return matchAccessMemberId(data, accessName);
  } catch {
    return null;
  }
}

/**
 * Load session ABAC for the logged-in multi-access member once per session.
 * GET /multiUser/permissions/{accessId}; later live updates via WS push.
 */
export const loadAbacSessionPermissions = createAsyncThunk(
  "auth/loadAbacSessionPermissions",
  async (_, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      const user = auth.user || {};
      const tokenStr = user.accessToken;
      if (!tokenStr) return thunkAPI.rejectWithValue({ message: "No Token" });

      const accessId = await resolveSessionAccessId(user, tokenStr);
      if (accessId == null) {
        console.log("[ABAC] session permissions skipped — access id not found", user);
        return { permissions: null, skipped: true, accessId: null };
      }

      const { data, status } = await axios.get(
        `/multiUser/permissions/${accessId}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      if (status.toString().startsWith("2")) {
        const permissions = extractPermissionsFromResponse(data, accessId);
        console.log("[ABAC] session permissions loaded", { accessId, permissions });
        return { data, status, accessId };
      }
      return thunkAPI.rejectWithValue(data);
    } catch (err) {
      if (!err.response) {
        console.error("err ===>", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { auth } = getState();
      const user = auth?.user;
      if (!user?.accessToken) return false;
      if (!isMultiAccessUser(user)) return false;
      if (auth.abacSessionLoaded) return false;
      if (auth.isFetchingAbacSession) return false;
      return true;
    },
  }
);

/** @deprecated Prefer loadAbacSessionPermissions — kept for existing callers. */
export const fetchAbacSession = loadAbacSessionPermissions;

export const getNavOptionsData = createAsyncThunk(
  "login/web-access",
  async (_, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let { data, status } = await axios.get(`/login/web-access`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const getLandingInfo = createAsyncThunk(
  "login/user/data",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let { data, status } = await axios.get(`users/panel/public/` + udata, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (status.toString().startsWith("2")) {
        return { ...data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const updateLandingInfo = createAsyncThunk(
  "update/landingInfo",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let systemId = auth.user.username;

      const { content, logo, icons, refId } = udata;

      const formData = new FormData();
      formData.append("logo", logo);
      Array.from(icons).forEach((icon, index) => {
        formData.append("icons", icon);
      });
      formData.append("content", content);

      const { data, status } = await axios.put(
        `/users/panel/${refId}?systemId=` + systemId,
        formData,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const createLandingInfo = createAsyncThunk(
  "create/landingInfo",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let systemId = auth.user.username;

      const { content, logo, icons, refId } = udata;

      const formData = new FormData();
      formData.append("logo", logo);
      Array.from(icons).forEach((icon, index) => {
        formData.append("icons", icon);
      });
      formData.append("content", content);

      let { data, status } = await axios.post(
        `/users/panel?systemId=` + systemId,
        formData,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearStateAuth: (state, { payload }) => {
      state.isFetching = false;
      state.accessDevice = false;
      state.otpScreen = false;
      state.confirmOtp = false;
      state.accountExpired = false;
      state.isError = false;
      state.errorMessage = "";
      state.isLoginSuccess = false;
    },
    setAbacPermissions: (state, { payload }) => {
      state.abacPermissions = payload ?? null;
      state.abacSessionLoaded = true;
      state.abacSessionOk = payload != null;
      state.isFetchingAbacSession = false;
    },
    /**
     * Apply live ABAC_PERMISSIONS_UPDATED from `/user/queue/abac-permissions`.
     * Replaces the full permissions tree so UI gates re-evaluate immediately.
     */
    applyAbacPermissionsPush: (state, { payload }) => {
      const event = payload && typeof payload === "object" ? payload : {};
      const accessId =
        event.accessId ??
        event.accessid ??
        state.abacAccessKey ??
        resolveAbacAccessId(state.user);

      const source =
        event.permissions != null
          ? { permissions: event.permissions }
          : event.data != null
            ? event.data
            : event;

      const next = extractPermissionsFromResponse(source, accessId);
      // Empty WS payloads must not wipe a good session (all-OFF still has keys).
      if (!next || typeof next !== "object" || Object.keys(next).length === 0) {
        const hasSession =
          state.abacPermissions != null &&
          typeof state.abacPermissions === "object" &&
          Object.keys(state.abacPermissions).length > 0;
        console.warn(
          "⚠️ [ABAC Auth] empty push ignored — " +
            (hasSession ? "keeping session permissions" : "no usable permissions"),
          { accessId, version: event.version ?? null }
        );
        return;
      }

      state.abacPermissions = next;
      if (accessId != null && accessId !== "") {
        state.abacAccessKey = accessId;
        if (state.user && typeof state.user === "object") {
          state.user = {
            ...state.user,
            accessId,
            multiUserEntryId: state.user.multiUserEntryId ?? accessId,
          };
        }
      }
      if (event.accessName || event.accessname) {
        const name = String(event.accessName ?? event.accessname).trim();
        if (name && state.user && typeof state.user === "object") {
          state.user = { ...state.user, accessName: name };
        }
      }
      state.abacSessionLoaded = true;
      state.abacSessionOk = true;
      state.isFetchingAbacSession = false;
      console.log("✅ [ABAC Auth] applyAbacPermissionsPush", {
        accessId: state.abacAccessKey,
        version: event.version ?? null,
        moduleKeys: Object.keys(state.abacPermissions),
      });
    },
    clearAbacSession: (state) => {
      resetAbacSessionFields(state);
    },
    resetAbacSession: (state) => {
      resetAbacSessionFields(state);
    },
    logoutAuth: () => {
      clearStaleAuthSession();

      localStorage.removeItem("savedSearch");
      localStorage.removeItem("dateRange");
      localStorage.removeItem("pinnedGraph");

      if (globalThis.stallTime) {
        clearInterval(globalThis.stallTime);
        globalThis.stallTime = null;
        globalThis.notificationTime = null;
      }

      return { ...initialState };
    },
    clearPanelUpdate: (state) => {
      state.isFetchingLoginPanelSave = false;
      state.isErrorUpdateLandingInfo = false;
      state.updateLandingInfoErrorMsg = "";
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(accessDeviceSkip.fulfilled, (state, action) => {
        let { data, status } = action.payload;

        state.accessDevice = false;
        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data);
        }
        state.isFetching = false;
        state.isAccessDeviceSuccess = true;
      })
      .addCase(accessDeviceSkip.pending, (state) => {
        state.isFetching = true;
        state.isAccessDeviceSuccess = false;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(accessDeviceSkip.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(accessDeviceThunk.fulfilled, (state, action) => {
        let { data, status } = action.payload;

        state.accessDevice = false;
        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data);
        }
        state.isFetching = false;
        state.isAccessDeviceSuccess = true;
      })
      .addCase(accessDeviceThunk.pending, (state) => {
        state.isFetching = true;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(accessDeviceThunk.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = action.payload
          ? "Device Access does not matched"
          : "Internal error";
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        let { data, status } = action.payload;

        state.otpScreen = false;
        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data);
        }
        state.isFetching = false;
        state.isOTPSuccess = true;
      })
      .addCase(verifyOTP.pending, (state) => {
        state.isFetching = true;
        state.isOTPSuccess = false;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage =
          (typeof action.payload === "string" ? action.payload : null) ||
          action.payload?.message ||
          action.payload?.data?.message ||
          (typeof action.payload?.data === "string" ? action.payload.data : null) ||
          action.payload?.error ||
          "OTP verification failed";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        let { data, status, requestUrl } = action.payload;

        // Stash jwt requestUrl immediately (even for 001 / 002 / 003)
        state.jwtLoginMeta = {
          requestUrl: requestUrl || "",
          response: null,
          capturedAt: Date.now(),
        };

        if (status === "001") {
          state.accessDevice = true;
        } else if (status === "002") {
          state.otpScreen = true;
        } else if (status === "003") {
          state.otpScreen = true;
        } else if (status === "403") {
          state.accountExpired = true;
        } else {
          applyLoginSuccess(state, data);
        }

        state.isFetching = false;
      })
      .addCase(loginUser.pending, (state) => {
        clearStaleAuthSession();
        state.user = {};
        state.jwtLoginMeta = null;
        state.isFetching = true;
        state.isLoginSuccess = false;
        state.isOTPSuccess = false;
        state.isAccessDeviceSuccess = false;
        state.errorMessage = "";
        state.isError = false;
        state.accessDevice = false;
        state.otpScreen = false;
        state.confirmOtp = false;
        state.accountExpired = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage =
          (typeof action.payload === "string" ? action.payload : null) ||
          action.payload?.message ||
          action.payload?.data?.message ||
          (typeof action.payload?.data === "string" ? action.payload.data : null) ||
          action.payload?.error ||
          "Login failed";
      })
      
      .addCase(getNavOptionsData.fulfilled, (state, action) => {
        state.navOptions = action.payload;
        state.isFetchingNavOptions = false;
        state.isnavOptionsFetchSuccess = true;
      })
      .addCase(getNavOptionsData.pending, (state) => {
        state.isFetchingNavOptions = true;
        state.isnavOptionsFetchSuccess = false;
        state.isnavOptionsFetchError = false;
        state.navOptionsFetchErrorMessage = "";
      })
      .addCase(getNavOptionsData.rejected, (state, action) => {
        state.navOptions = {};
        state.isFetchingNavOptions = false;
        state.isnavOptionsFetchError = true;
        state.navOptionsFetchErrorMessage = action.payload
          ? action.payload.message
          : "Internal error";
      })
      .addCase(getLandingInfo.pending, (state, action) => {
        state.isFetchingLandingInfo = true;
      })
      .addCase(getLandingInfo.fulfilled, (state, action) => {
        state.isFetchingLandingInfo = false;
      })
      .addCase(getLandingInfo.rejected, (state, action) => {
        state.isFetchingLandingInfo = false;
      })
      .addCase(updateLandingInfo.pending, (state, action) => {
        state.isFetchingLoginPanelSave = true;
      })
      .addCase(updateLandingInfo.fulfilled, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isErrorUpdateLandingInfo = false;
        state.updateLandingInfoErrorMsg = "";
      })
      .addCase(updateLandingInfo.rejected, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isErrorUpdateLandingInfo = true;
        state.updateLandingInfoErrorMsg = action.payload
          ? action.payload.message
          : "Error while updating panel";
      })
      .addCase(createLandingInfo.pending, (state, action) => {
        state.isFetchingLoginPanelSave = true;
      })
      .addCase(createLandingInfo.fulfilled, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isSuccessCreateLandingInfo = true;
      })
      .addCase(createLandingInfo.rejected, (state, action) => {
        state.isFetchingLoginPanelSave = false;
        state.isSuccessCreateLandingInfo = false;
      })
      .addCase(loadAbacSessionPermissions.pending, (state) => {
        state.isFetchingAbacSession = true;
        console.log("🟣 [ABAC Auth] loadAbacSessionPermissions pending…");
      })
      .addCase(loadAbacSessionPermissions.fulfilled, (state, action) => {
        state.isFetchingAbacSession = false;
        state.abacSessionLoaded = true;
        if (action.payload?.skipped) {
          state.abacPermissions = null;
          state.abacAccessKey = null;
          state.abacSessionOk = false;
          console.warn("⚠️ [ABAC Auth] Session bootstrap skipped (no accessId)");
          return;
        }
        const accessId =
          action.payload?.accessId ??
          resolveAbacAccessId(state.user) ??
          state.abacAccessKey;
        state.abacAccessKey = accessId ?? null;
        if (accessId != null && state.user && typeof state.user === "object") {
          state.user = {
            ...state.user,
            accessId,
            multiUserEntryId: state.user.multiUserEntryId ?? accessId,
          };
        }
        state.abacPermissions = extractPermissionsFromResponse(
          action.payload?.data ?? action.payload,
          accessId
        );
        state.abacSessionOk = state.abacPermissions != null;
        console.log("✅ [ABAC Auth] Session bootstrap OK", {
          accessId: state.abacAccessKey,
          moduleKeys: state.abacPermissions
            ? Object.keys(state.abacPermissions)
            : [],
        });
      })
      .addCase(loadAbacSessionPermissions.rejected, (state) => {
        state.isFetchingAbacSession = false;
        state.abacSessionLoaded = true;
        state.abacSessionOk = false;
        // Fail open: leave permissions null so gates allow until WS push / retry.
        state.abacPermissions = null;
        console.warn("⚠️ [ABAC Auth] Session bootstrap rejected — fail-open until WS push");
      });
  },
});

export const {
  clearStateAuth,
  logoutAuth,
  clearPanelUpdate,
  setAbacPermissions,
  applyAbacPermissionsPush,
  clearAbacSession,
  resetAbacSession,
} = authSlice.actions;

/**
 * Normalize and apply a STOMP ABAC permissions push.
 * Accepts raw message body or wrapped `{ data: { … } }` shapes.
 * @returns {boolean} true when an update was dispatched
 */
export function handleAbacPermissionsPushEvent(dispatch, raw) {
  if (!dispatch || raw == null) return false;

  let event = raw;
  if (typeof raw === "string") {
    try {
      event = JSON.parse(raw);
    } catch {
      return false;
    }
  }
  if (!event || typeof event !== "object") return false;

  // Unwrap common envelopes
  if (
    event.data &&
    typeof event.data === "object" &&
    (event.data.permissions != null ||
      event.data.type ||
      event.data.accessId != null)
  ) {
    event = event.data;
  }

  const type = String(event.type || event.eventType || "").toUpperCase();
  const hasPermissions =
    event.permissions != null ||
    (event.data && event.data.permissions != null);

  if (type && type !== "ABAC_PERMISSIONS_UPDATED" && !hasPermissions) {
    console.log("[ABAC WS] skipped — unexpected event type:", type);
    return false;
  }
  if (!type && !hasPermissions) return false;

  const accessId =
    event.accessId ?? event.accessid ?? resolveAbacAccessId(null);
  const source =
    event.permissions != null
      ? { permissions: event.permissions }
      : event.data != null
        ? event.data
        : event;
  const preview = extractPermissionsFromResponse(source, accessId);
  if (!preview || Object.keys(preview).length === 0) {
    console.log("[ABAC WS] skipped — no usable permissions in event", event);
    return false;
  }

  console.log("[ABAC WS] applied permissions update", {
    accessId,
    version: event.version ?? event.updatedAt ?? null,
    accessName: event.accessName,
    updatedBy: event.updatedBy,
    updatedAt: event.updatedAt,
    permissions: preview,
  });
  dispatch(applyAbacPermissionsPush(event));
  return true;
}

export function clearState() {
  return async (dispatch) => {
    dispatch(clearStateAuth());
  };
}

export function clearLogoutAuthState() {
  return async (dispatch) => {
    dispatch(logoutAuth());
  };
}


export const authSelector = (state) => state.auth;

export default authSlice.reducer;
